import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Building2, ClipboardList, Loader2, PenLine, Sparkles } from "lucide-react";

import { fetchOpciDioStatus } from "@/api/client";
import { Button } from "@/components/ui/button";
import { useProjectStore } from "@/features/projects/useProjectStore";
import { DocCard } from "./OpciDioDocsPanel";
import { SituacijaDetails, SituacijaSummaryBadge } from "./SituacijaPanel";
import { KonzolaRow } from "./KonzolaRow";
import {
  GEODETSKI_ROW,
  NACRTI_ROWS,
  PROJEKT_ROWS,
  docTypeLabel,
  docsOfSlot,
  docsOfTypes,
  expectedTypeForSlot,
  expectedTypesForSlot,
  mismatchesForRow,
  projektRowState,
  unclaimedDocs,
  type IndexedDoc,
  type ProjektRowDef,
  type RowMismatch,
} from "./konzolaRows";
import { applyManualFieldsToDraft } from "./prefillToDraft";
import { NacrtVisionPanel } from "./NacrtVisionPanel";
import { useIntakeStore } from "./useIntakeStore";
import { useRecognizeDocs, type OpciDioParams } from "./useRecognizeDocs";

/**
 * F1 — the Donesi KONZOLA (successor of the read-only cockpit): what Mapa 1
 * expects, as actionable rows. Every row = one item = one place for input,
 * status and evidence:
 *
 *   ① PROJEKT   — required form data; inline mini-entry on the row
 *   ② OPĆI DIO  — upload slots from /api/opci-dio/status; row = drop-zone
 *   ③ TEKST     — geodetski elaborat (drop-zone) + opis zahvata
 *   ④ NACRTI    — situacija / tlocrti / presjeci / pročelja (drop-zones)
 *
 * A targeted drop sends ``expected_type`` to the recognizer; a conflicting
 * classification comes back as a LOUD message on the row ("ovo izgleda kao
 * X — potvrdi ili ispravi"), never a silent mis-file. Documents no row
 * claims stay visible under "Ostali dokumenti".
 */
export function DonesiKonzola({
  projectId,
  params,
}: {
  projectId: string;
  params: OpciDioParams;
}) {
  const navigate = useNavigate();
  const intake = useIntakeStore((s) => s.get(projectId));
  const setStatus = useIntakeStore((s) => s.setOpciDioStatus);
  const removeDoc = useIntakeStore((s) => s.removeRecognizedDoc);
  const overrideType = useIntakeStore((s) => s.overrideDocType);
  const project = useProjectStore((s) =>
    s.projects.find((p) => p.id === projectId),
  );
  const { addPdfFiles } = useRecognizeDocs(projectId, params);

  // Which row is mid-upload + the last failure per row — the row must show
  // its own outcome, not a page-level toast far away from the drop.
  const [busyRows, setBusyRows] = useState<Record<string, boolean>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, string | null>>({});

  const docs = intake.recognizedDocs;

  // Type/override signature: an override changes a doc's type but not the
  // count, so keying on length alone would leave the status stale.
  const docsSig = docs
    .map(
      (d) =>
        `${d.document_type}${(d as Record<string, unknown>)._user_overridden ? "!" : ""}`,
    )
    .join("|");

  const query = useQuery({
    queryKey: [
      "opci-status",
      projectId,
      params.building_type,
      params.building_subtype ?? "",
      params.work_type ?? "",
      docsSig,
      intake.situacija ? 1 : 0,
    ],
    queryFn: () =>
      fetchOpciDioStatus({
        ...params,
        situacija: intake.situacija ?? undefined,
        recognized_docs: docs,
      }),
    staleTime: 10_000,
    retry: 1,
  });

  // Mirror into the shared store so the Provjeri panel stays in sync.
  useEffect(() => {
    if (query.data) setStatus(projectId, query.data);
  }, [query.data, projectId, setStatus]);

  const rows = query.data?.rows ?? intake.opciDioStatus?.rows ?? [];

  const uploadTo = async (rowKey: string, expectedType: string, files: File[]) => {
    setBusyRows((b) => ({ ...b, [rowKey]: true }));
    setRowErrors((e) => ({ ...e, [rowKey]: null }));
    try {
      const outcomes = await addPdfFiles(files, { expectedType });
      const failed = outcomes.filter((o) => !o.ok);
      if (failed.length) {
        setRowErrors((e) => ({
          ...e,
          [rowKey]: failed
            .map((f) => `${f.file}: ${f.error ?? "prepoznavanje nije uspjelo"}`)
            .join(" · "),
        }));
      }
    } finally {
      setBusyRows((b) => ({ ...b, [rowKey]: false }));
    }
  };

  // ① Projekt
  const projectData = (project?.projectData ?? {}) as Record<string, unknown>;
  const projektStates = PROJEKT_ROWS.map((row) => ({
    row,
    ...projektRowState(row, projectData, params.building_type),
  }));

  // ② Opći dio — upload slots the architect provides. Slot 1.14 (geodetski
  // elaborat) is OWNED by the Tekst group: one item = one place.
  const provideRows = rows.filter(
    (r) =>
      r.fulfilled_by === "upload" &&
      r.state !== "nije_potrebno" &&
      r.slot !== "1.14",
  );
  const generateCount = rows.filter(
    (r) => r.fulfilled_by === "generate" && r.state !== "nije_potrebno",
  ).length;
  const geodetskiSlotRow = rows.find((r) => r.slot === "1.14");

  // ③/④ — presence from recognized document types + the draft.
  const uploadedTypes = useMemo(
    () => new Set(docs.map((d) => String(d.document_type ?? ""))),
    [docs],
  );
  const geodetskiPresent =
    uploadedTypes.has("geodetski_elaborat") ||
    geodetskiSlotRow?.state === "prepoznato";
  const opisPresent = Boolean(projectData.zahvat_opis);

  // Headline counts — optional slots inform but never count as "fali".
  const haveCount =
    projektStates.filter((i) => i.present).length +
    provideRows.filter((r) => r.state === "prepoznato").length +
    (geodetskiPresent ? 1 : 0) +
    (opisPresent ? 1 : 0) +
    NACRTI_ROWS.filter((n) => n.types.some((t) => uploadedTypes.has(t))).length;
  const missingCount =
    projektStates.filter((i) => !i.present).length +
    provideRows.filter((r) => r.state === "nedostaje" && r.required).length +
    (geodetskiPresent ? 0 : 1) +
    (opisPresent ? 0 : 1) +
    NACRTI_ROWS.filter((n) => !n.types.some((t) => uploadedTypes.has(t))).length;

  const claimedSlots = [...provideRows.map((r) => r.slot), "1.14"];
  const ostali = unclaimedDocs(docs, claimedSlots);

  const evidenceSummary = (list: IndexedDoc[]) =>
    list.length
      ? list
          .map(({ doc }) => {
            const conf = doc.classification_confidence;
            const name = doc._source_file ?? "dokument.pdf";
            return conf != null ? `${name} · ${Math.round(conf * 100)} %` : name;
          })
          .join(" · ")
      : undefined;

  const docCards = (list: IndexedDoc[]) => (
    <ul className="flex flex-col gap-2 py-1">
      {list.map(({ doc, index }) => (
        <DocCard
          key={`${doc._source_file ?? "doc"}-${index}`}
          doc={doc}
          onOverride={(t) => overrideType(projectId, index, t)}
          onRemove={() => removeDoc(projectId, index)}
        />
      ))}
    </ul>
  );

  // B1 — per drawing, offer AI reading (vision) as a complement. Only drawings
  // with a persisted ``doc_id`` can be read (the file lives at data/uploads).
  const visionPanels = (list: IndexedDoc[]) => (
    <>
      {list.map(({ doc, index }) =>
        doc.doc_id ? (
          <NacrtVisionPanel
            key={`vis-${doc.doc_id ?? index}`}
            projectId={projectId}
            docId={String(doc.doc_id)}
            docType={String(doc.document_type ?? "")}
          />
        ) : null,
      )}
    </>
  );

  const mismatchNotices = (rowKey: string, mismatches: RowMismatch[]) => (
    <>
      {rowErrors[rowKey] && (
        <div className="mx-2 mb-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[0.78rem] text-red-700">
          {rowErrors[rowKey]} — dokument je spremljen pod „Ostali dokumenti";
          vrstu mu možeš dodijeliti ručno.
        </div>
      )}
      {mismatches.map((m) => (
        <div
          key={`mm-${m.index}`}
          className="mx-2 mb-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[0.78rem] text-amber-800"
        >
          <span className="font-semibold">
            {m.doc._source_file ?? "Dokument"}: ovo izgleda kao „
            {docTypeLabel(m.got)}"
          </span>{" "}
          — potvrdi ili ispravi.
          <span className="mt-1 flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded border border-amber-300 bg-white px-2 py-0.5 text-[0.74rem] font-semibold text-amber-900 hover:bg-amber-100"
              onClick={() => overrideType(projectId, m.index, m.expected)}
            >
              Ovo je {docTypeLabel(m.expected)}
            </button>
            <button
              type="button"
              className="rounded border border-amber-300 bg-white px-2 py-0.5 text-[0.74rem] font-semibold text-amber-900 hover:bg-amber-100"
              onClick={() => overrideType(projectId, m.index, m.got)}
            >
              Točno, ovo je {docTypeLabel(m.got)}
            </button>
          </span>
        </div>
      ))}
    </>
  );

  return (
    <div className="mb-4 rounded-lg border border-ind-200 bg-ind-50/40 p-4">
      <div className="mb-2 flex items-center gap-2">
        <ClipboardList className="h-4 w-4 text-ind-700" />
        <h3 className="text-[0.9rem] font-bold text-stone-800">
          Što projekt treba
        </h3>
        {query.isFetching ? (
          <span className="ml-auto inline-flex items-center gap-1.5 text-[0.76rem] text-stone-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> osvježavam…
          </span>
        ) : (
          <span className="ml-auto text-[0.78rem] text-stone-500">
            {haveCount} spremno
            {missingCount > 0 && (
              <>
                {" · "}
                <span className="font-semibold text-amber-700">
                  {missingCount} fali
                </span>
              </>
            )}
          </span>
        )}
      </div>
      <p className="mb-3 text-[0.76rem] text-stone-400">
        Svaki redak je jedno mjesto: povuci PDF na njega, upiši podatak ili
        klikni za dokaz što je pročitano.
      </p>

      {/* ① Projekt */}
      <KonzolaSection title="1 · Projekt (podaci)">
        {projektStates.map(({ row, present, missing }) => (
          <KonzolaRow
            key={row.label}
            label={row.label}
            present={present}
            summary={
              present
                ? projektValueSummary(row, projectData, params.building_type)
                : row.inline
                  ? "upiši ovdje"
                  : "dopuni na koraku Provjeri"
            }
            defaultOpen={false}
          >
            {row.inline ? (
              <ProjektMiniForm
                projectId={projectId}
                row={row}
                projectData={projectData}
              />
            ) : row.fields.some((f) => f.name === "ured_name") ? (
              // Ured & projektant live at the OFFICE level — set once in
              // Postavke, hydrated into every project (fill-empty).
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 py-1 text-[0.78rem] text-stone-500">
                <Building2 className="h-3.5 w-3.5 shrink-0 text-ind-500" />
                {present
                  ? "Podaci ureda su tu — jednom postavljeni u Postavkama vrijede za sve projekte."
                  : `Fali: ${missing.map((f) => f.label).join(", ")}. Postavi ih jednom u Postavkama ureda — vrijede za sve projekte.`}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/postavke")}
                >
                  Otvori Postavke ureda →
                </Button>
              </p>
            ) : (
              <p className="py-1 text-[0.78rem] text-stone-500">
                {present
                  ? "Uneseno — detalje mijenjaš na koraku Provjeri."
                  : `Fali: ${missing.map((f) => f.label).join(", ")} — unosi se na koraku Provjeri.`}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="ml-2"
                  onClick={() => navigate(`/project/${projectId}/provjeri`)}
                >
                  Otvori Provjeri →
                </Button>
              </p>
            )}
          </KonzolaRow>
        ))}
        <li className="px-2 pt-1">
          <button
            type="button"
            className="text-[0.72rem] font-semibold text-ind-700 hover:text-ind-800"
            onClick={() => navigate(`/project/${projectId}/provjeri`)}
          >
            Dopuni sve podatke na koraku Provjeri →
          </button>
        </li>
      </KonzolaSection>

      {/* ② Opći dio */}
      <KonzolaSection title="2 · Opći dio (dokumenti)" hint="svaki redak = drop-zona">
        {provideRows.length === 0 && (
          <li className="px-2 text-[0.78rem] text-stone-400">
            {query.isFetching
              ? "Učitavam popis potrebnih dokumenata…"
              : "Popis potrebnih dokumenata stiže nakon prve provjere spremnosti."}
          </li>
        )}
        {provideRows.map((r) => {
          const slotDocs = docsOfSlot(docs, r.slot);
          const expected = expectedTypeForSlot(r.slot);
          return (
            <KonzolaRow
              key={r.slot}
              label={r.name}
              present={r.state === "prepoznato"}
              optional={!r.required}
              summary={evidenceSummary(slotDocs)}
              uploading={busyRows[`slot-${r.slot}`]}
              onFiles={
                expected
                  ? (files) => void uploadTo(`slot-${r.slot}`, expected, files)
                  : undefined
              }
              notice={mismatchNotices(
                `slot-${r.slot}`,
                mismatchesForRow(docs, expectedTypesForSlot(r.slot)),
              )}
            >
              {slotDocs.length > 0 ? docCards(slotDocs) : undefined}
            </KonzolaRow>
          );
        })}
        {generateCount > 0 && (
          <li className="flex items-center gap-1.5 px-2 pt-1 text-[0.78rem] text-stone-500">
            <Sparkles className="h-3 w-3 shrink-0 text-ind-400" />
            {generateCount === 1
              ? "1 izjavu aplikacija generira sama"
              : `${generateCount} izjava aplikacija generira sama`}
          </li>
        )}
      </KonzolaSection>

      {/* ③ Tekst */}
      <KonzolaSection title="3 · Tekst">
        <KonzolaRow
          label={GEODETSKI_ROW.label}
          present={geodetskiPresent}
          summary={evidenceSummary(docsOfTypes(docs, GEODETSKI_ROW.types))}
          uploading={busyRows[GEODETSKI_ROW.key]}
          onFiles={(files) =>
            void uploadTo(GEODETSKI_ROW.key, GEODETSKI_ROW.expectedType, files)
          }
          notice={mismatchNotices(
            GEODETSKI_ROW.key,
            mismatchesForRow(docs, GEODETSKI_ROW.types),
          )}
        >
          {docsOfTypes(docs, GEODETSKI_ROW.types).length > 0
            ? docCards(docsOfTypes(docs, GEODETSKI_ROW.types))
            : undefined}
        </KonzolaRow>
        <KonzolaRow
          label="Opis zahvata"
          present={opisPresent}
          summary={
            <span className="inline-flex min-w-0 max-w-full items-center gap-2">
              {opisPresent && (
                <span className="min-w-0 truncate">
                  {String(projectData.zahvat_opis)}
                </span>
              )}
              <button
                type="button"
                className="inline-flex shrink-0 items-center gap-1 font-semibold text-ind-700 hover:text-ind-800"
                onClick={(e) => {
                  e.stopPropagation();
                  const editor = document.getElementById("opis-editor");
                  editor?.scrollIntoView({ behavior: "smooth", block: "center" });
                  editor?.querySelector("textarea")?.focus({ preventScroll: true });
                }}
              >
                <PenLine className="h-3 w-3" />
                {opisPresent ? "doradi gore" : "upiši / izdiktiraj gore"}
              </button>
            </span>
          }
        />
        <li className="px-2 pt-1 text-[0.72rem] text-stone-400">
          Tehnički opis i iskazi generiraju se iz podataka i situacije.
        </li>
      </KonzolaSection>

      {/* ④ Nacrti */}
      <KonzolaSection title="4 · Nacrti" hint="isti drop-obrazac">
        {NACRTI_ROWS.map((def) => {
          const rowDocs = docsOfTypes(docs, def.types);
          const isSituacija = def.key === "situacija";
          return (
            <KonzolaRow
              key={def.key}
              label={def.label}
              present={rowDocs.length > 0}
              summary={
                isSituacija ? (
                  <span className="inline-flex items-center gap-2">
                    <SituacijaSummaryBadge report={intake.situacija} />
                    {evidenceSummary(rowDocs)}
                  </span>
                ) : (
                  evidenceSummary(rowDocs)
                )
              }
              uploading={busyRows[def.key]}
              onFiles={(files) => void uploadTo(def.key, def.expectedType, files)}
              notice={mismatchNotices(def.key, mismatchesForRow(docs, def.types))}
            >
              {isSituacija && (intake.situacija || rowDocs.length > 0) ? (
                <>
                  <SituacijaDetails projectId={projectId} />
                  {rowDocs.length > 0 && docCards(rowDocs)}
                  {visionPanels(rowDocs)}
                </>
              ) : rowDocs.length > 0 ? (
                <>
                  {docCards(rowDocs)}
                  {visionPanels(rowDocs)}
                </>
              ) : undefined}
            </KonzolaRow>
          );
        })}
      </KonzolaSection>

      {/* Documents no row claims — visible, never a black hole. */}
      {ostali.length > 0 && (
        <KonzolaSection title="Ostali dokumenti" hint="prepoznato izvan popisa gore">
          <li className="px-2 pb-1 text-[0.74rem] text-stone-400">
            Spremljeni su i idu uz projekt; krivu vrstu ispravi na kartici.
          </li>
          <li>{docCards(ostali)}</li>
        </KonzolaSection>
      )}
    </div>
  );
}

function KonzolaSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="mb-1 flex items-baseline gap-2">
        <h4 className="text-[0.72rem] font-bold uppercase tracking-[0.04em] text-stone-500">
          {title}
        </h4>
        {hint && <span className="text-[0.68rem] text-stone-300">— {hint}</span>}
      </div>
      <ul className="flex flex-col">{children}</ul>
    </div>
  );
}

/** Compact "what's in there" for a filled Projekt row. */
function projektValueSummary(
  row: ProjektRowDef,
  projectData: Record<string, unknown>,
  buildingTypeFallback?: string | null,
): string {
  return row.fields
    .map((f) => {
      const v =
        f.name === "building_type"
          ? (projectData[f.name] ?? buildingTypeFallback)
          : projectData[f.name];
      if (v == null || v === "") return null;
      // Backend enum keys (obiteljska_kuca) are pipeline vocabulary — the
      // architect reads words, not keys.
      return f.name === "building_type"
        ? String(v).replaceAll("_", " ")
        : String(v);
    })
    .filter(Boolean)
    .join(" · ");
}

/**
 * K4 — inline mini-entry for a Projekt row (2–3 fields), writing through
 * the SAME draft-patch path uploads use (``prefillToDraft``). The Provjeri
 * form stays the validator — this is a quick fill, not a second form.
 */
function ProjektMiniForm({
  projectId,
  row,
  projectData,
}: {
  projectId: string;
  row: ProjektRowDef;
  projectData: Record<string, unknown>;
}) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      row.fields.map((f) => [f.name, String(projectData[f.name] ?? "")]),
    ),
  );
  const [saved, setSaved] = useState(false);

  const save = () => {
    const patch: Record<string, unknown> = {};
    for (const f of row.fields) {
      const raw = values[f.name]?.trim() ?? "";
      if (raw === "") continue; // never blank out an existing value
      patch[f.name] = f.type === "number" ? Number(raw) : raw;
    }
    applyManualFieldsToDraft(projectId, patch);
    setSaved(true);
  };

  return (
    <div className="flex flex-wrap items-end gap-2 py-1.5">
      {row.fields.map((f) => (
        <label key={f.name} className="flex flex-col gap-0.5">
          <span className="text-[0.7rem] text-stone-500">{f.label}</span>
          <input
            type={f.type}
            value={values[f.name] ?? ""}
            placeholder={f.placeholder}
            onChange={(e) => {
              setSaved(false);
              setValues((v) => ({ ...v, [f.name]: e.target.value }));
            }}
            className="w-40 rounded border border-stone-300 bg-white px-2 py-1 text-[0.8rem] text-stone-800"
          />
        </label>
      ))}
      <Button type="button" size="sm" onClick={save}>
        Spremi
      </Button>
      {saved && (
        <span className="text-[0.74rem] text-green-700">
          Spremljeno u nacrt projekta — provjeri na koraku Provjeri.
        </span>
      )}
    </div>
  );
}
